import dayjs, { type Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import type { BrandConfig } from '@/brand/types';

dayjs.extend(isoWeek);

// Пресеты периода. Раньше эти же одиннадцать вариантов лежали switch-case'ом
// плюс одиннадцатью копипастными блоками разметки, а активный пресет держался
// локальным стейтом: после перезагрузки или программной смены диапазона
// (drill-down в статистике сужает период) галочка уезжала не туда.
//
// Теперь диапазоны — данные, а активный пресет ВЫВОДИТСЯ из текущих from/to,
// то есть галочка всегда соответствует тому, что реально выбрано.

// Подпись пресета не хранится рядом с диапазоном: ключ словаря совпадает с
// id (common.datePresets.<id>), поэтому её резолвит сам DatePicker.
export type DatePresetId =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'prevWeek'
  | 'last7Days'
  | 'thisMonth'
  | 'prevMonth'
  | 'last30Days'
  | 'last90Days'
  | 'last365Days';

export type DateRange = { from: Date; to: Date };

export type DatePreset = {
  id: DatePresetId;
  // Разделитель перед пунктом — группы «сегодня/вчера», «недели», «месяцы».
  separated?: boolean;
  range: (now: Dayjs) => DateRange;
};

// Диапазоны перенесены из прежнего switch без изменений: «последние N дней»
// считаются от начала дня N дней назад до конца сегодняшнего, недели — по
// ISO (с понедельника).
export const DATE_PRESETS: DatePreset[] = [
  {
    id: 'today',
    range: (now) => ({ from: now.toDate(), to: now.toDate() }),
  },
  {
    id: 'yesterday',
    range: (now) => {
      const yesterday = now.subtract(1, 'day').toDate();
      return { from: yesterday, to: yesterday };
    },
  },
  {
    id: 'thisWeek',
    separated: true,
    range: (now) => ({ from: now.startOf('isoWeek').toDate(), to: now.endOf('isoWeek').toDate() }),
  },
  {
    id: 'prevWeek',
    range: (now) => {
      const lastWeek = now.subtract(1, 'week');
      return { from: lastWeek.startOf('isoWeek').toDate(), to: lastWeek.endOf('isoWeek').toDate() };
    },
  },
  {
    id: 'last7Days',
    range: (now) => ({
      from: now.subtract(7, 'day').startOf('day').toDate(),
      to: now.endOf('day').toDate(),
    }),
  },
  {
    id: 'thisMonth',
    separated: true,
    range: (now) => ({ from: now.startOf('month').toDate(), to: now.endOf('month').toDate() }),
  },
  {
    id: 'prevMonth',
    range: (now) => {
      const lastMonth = now.subtract(1, 'month');
      return { from: lastMonth.startOf('month').toDate(), to: lastMonth.endOf('month').toDate() };
    },
  },
  {
    id: 'last30Days',
    range: (now) => ({
      from: now.subtract(30, 'day').startOf('day').toDate(),
      to: now.endOf('day').toDate(),
    }),
  },
  {
    id: 'last90Days',
    range: (now) => ({
      from: now.subtract(90, 'day').startOf('day').toDate(),
      to: now.endOf('day').toDate(),
    }),
  },
  {
    id: 'last365Days',
    range: (now) => ({
      from: now.subtract(365, 'day').startOf('day').toDate(),
      to: now.endOf('day').toDate(),
    }),
  },
];

// Состав для активного бренда: exclude, а не include — новый пресет появляется
// у всех сам. У WapClick убраны недели и месяцы (замечание 31.07), остались
// «сегодня/вчера» и «последние N дней».
//
// Разделители пересчитываются после фильтра: у первого пункта черта сверху не
// нужна, а группы могли схлопнуться.
export const resolveDatePresets = (config: BrandConfig): DatePreset[] => {
  const excluded = new Set(config.filters.datePresets?.exclude ?? []);

  return DATE_PRESETS.filter((preset) => !excluded.has(preset.id)).map((preset, index) => ({
    ...preset,
    separated: index > 0 && preset.separated,
  }));
};

// Сравнение по календарным дням: пресеты задают границы то началом/концом дня,
// то моментом «сейчас», а партнёру важны именно даты.
const sameDay = (a: Date | null, b: Date | null): boolean =>
  a !== null && b !== null && dayjs(a).isSame(dayjs(b), 'day');

// Отмечает первый пресет с таким же диапазоном. Пресеты не взаимно
// исключающие: в отдельные дни два варианта дают один период — 31 июля
// «Последние 30 дней» совпадают с «Этот месяц», 7-го числа «Последние 7 дней»
// могут совпасть с «Эта неделя». Тогда галочка встанет на том, что выше в
// списке, хотя партнёр кликал другой пункт. Диапазон при этом верный, а
// альтернатива — запоминать выбранный id — врала в более частых случаях
// (перезагрузка страницы, drill-down в статистике меняет период программно).
//
// Искать нужно среди пресетов БРЕНДА, а не всех: у wapclick убраны недели и
// месяцы, и 31-го числа «Последние 30 дней» совпадали с исключённым «Этот
// месяц», который в общем списке выше, — в результате не подсвечивался ни один
// пункт. Дефолт оставлен полным списком: вызовы без бренда (тесты) сравнивают
// именно его.
export const matchPreset = (
  from: Date | null,
  to: Date | null,
  presets: DatePreset[] = DATE_PRESETS,
): DatePresetId | null => {
  if (!from || !to) return null;
  const now = dayjs();
  const hit = presets.find((preset) => {
    const range = preset.range(now);
    return sameDay(range.from, from) && sameDay(range.to, to);
  });

  return hit?.id ?? null;
};
